import api, { getErrorMessage } from './api';

const organizationsService = {
  /**
   * Get all organizations (admin only)
   * @returns {Promise<Array>} - Organizations list
   */
  getAllOrganizations: async () => {
    try {
      const response = await api.get('/api/v1/admin/organizations/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Alias for getAllOrganizations (admin only)
   * @returns {Promise<Array>} - Organizations list
   */
  list: async () => {
    return organizationsService.getAllOrganizations();
  },

  /**
   * Create organization (admin only)
   * @param {object} organizationData - Organization data
   * @param {string} organizationData.name - Organization name
   * @param {string} organizationData.description - Description
   * @param {boolean} organizationData.is_active - Is active
   * @param {number} organizationData.default_nomination_recipient_points - Default points for recipient
   * @param {number} organizationData.default_nomination_sender_points - Default points for sender
   * @returns {Promise<object>} - Created organization
   */
  createOrganization: async (organizationData) => {
    try {
      const response = await api.post('/api/v1/admin/organizations/', organizationData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create Workvivo-integrated organization (admin only)
   * @param {object} organizationData - Organization data with Workvivo config
   * @param {string} organizationData.name - Organization name
   * @param {string} organizationData.description - Description
   * @param {string} organizationData.workvivo_tenant_url - Workvivo tenant URL
   * @param {string} organizationData.workvivo_scim_token - Workvivo SCIM token
   * @returns {Promise<object>} - Created organization
   */
  createWorkvivoOrganization: async (organizationData) => {
    try {
      const response = await api.post('/api/v1/admin/organizations/workvivo', organizationData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get organization by ID (admin only)
   * @param {string} organizationId - Organization ID
   * @returns {Promise<object>} - Organization data
   */
  getOrganizationById: async (organizationId) => {
    try {
      const response = await api.get(`/api/v1/admin/organizations/${organizationId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update organization (admin only)
   * @param {string} organizationId - Organization ID
   * @param {object} organizationData - Organization data to update
   * @returns {Promise<object>} - Updated organization
   */
  updateOrganization: async (organizationId, organizationData) => {
    try {
      const response = await api.put(`/api/v1/admin/organizations/${organizationId}`, organizationData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete organization (admin only)
   * @param {string} organizationId - Organization ID
   * @returns {Promise<object>} - Success message
   */
  deleteOrganization: async (organizationId) => {
    try {
      const response = await api.delete(`/api/v1/admin/organizations/${organizationId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get stored Workvivo credentials metadata for an organization (admin only)
   * Returns base_url, workvivo_id, has_token, and a masked token_hint.
   * @param {string} organizationId - Organization ID
   * @returns {Promise<object>} - Stored credential metadata
   */
  getWorkvivoCredentials: async (organizationId) => {
    try {
      const response = await api.get(`/api/v1/admin/organizations/${organizationId}/workvivo-credentials`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Resync users from Workvivo for an organization (admin only).
   * Credentials are optional – backend falls back to stored credentials.
   * @param {string} organizationId - Organization ID
   * @param {object} credentials - Optional Workvivo credentials override
   * @returns {Promise<object>} - Sync results
   */
  resyncWorkvivo: async (organizationId, credentials) => {
    try {
      const response = await api.post(`/api/v1/admin/organizations/${organizationId}/resync`, credentials);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get SSO and SCIM integration status for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<object>} - { sso: {...}, scim: {...} }
   */
  getIntegrations: async (organizationId) => {
    try {
      const response = await api.get(`/api/v1/admin/organizations/${organizationId}/integrations`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  uploadLogo: async (orgId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/api/v1/admin/organizations/${orgId}/customization/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  uploadWidgetLogo: async (orgId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/api/v1/admin/organizations/${orgId}/customization/upload-widget-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  uploadBackground: async (orgId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/api/v1/admin/organizations/${orgId}/customization/upload-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  uploadLoginLogo: async (orgId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/api/v1/admin/organizations/${orgId}/customization/upload-login-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  deleteAsset: async (orgId, kind) => {
    try {
      const response = await api.delete(`/api/v1/admin/organizations/${orgId}/customization/${kind}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Set or clear an organization's subdomain (superuser only).
   * Pass null/empty string to clear.
   */
  updateSubdomain: async (orgId, subdomain) => {
    try {
      const response = await api.patch(
        `/api/v1/admin/organizations/${orgId}/subdomain`,
        { subdomain: subdomain || null },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // Alias methods for compatibility
  create: async (data) => organizationsService.createOrganization(data),
  update: async (id, data) => organizationsService.updateOrganization(id, data),
  delete: async (id) => organizationsService.deleteOrganization(id),
  get: async (id) => organizationsService.getOrganizationById(id),
};

export default organizationsService;
